import {
  getCoreRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { cn } from 'noddi-ui-common';
import { useCallback, useState } from 'react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import type { FilterControl } from '../../../types/filterControl';
import { NoddiPaginationState } from '../../../types/pagination';
import { NoddiResponsiveCard } from '../../Cards/NoddiResponsiveCard';
import { NoddiLinearProgressLoader } from '../../Feedback/NoddiLinearProgressLoader';
import { BaseTable } from './components/BaseTable';
import { MobileFilterDrawer } from './components/FilterSidebar/FilterSidebar';
import {
  RowSelectionAction,
  RowSelectionActionBar,
  RowSelectionActionButtons
} from './components/RowSelectionActionBar';
import { TableFilterHeader } from './components/TableFilterHeader';
import { TablePagination } from './components/TablePagination';
import { FilterUnit } from './filterUnits';
import { NoddiTableTranslations, resolveNoddiTableTranslations } from './tableTranslationTypes';
import { BaseTableCommonProps } from './types';
import { useGetProcessedColumns } from './useGetProcessedColumns';

export interface NoddiTableProps<TData, TValue, TOrderingKey extends string = string> extends BaseTableCommonProps<
  TData,
  TValue,
  TOrderingKey
> {
  pagination: NoddiPaginationState;
  setPagination: (pagination: NoddiPaginationState) => void;
  totalCount: number | undefined;
  isFetching: boolean;
  searchPlaceholder?: string;
  /**
   * Sidebar controls, quick filter and date range, each bound to the params it writes — build with
   * `filterUnitFor` builders resolved by `useFilterUnits`, and spread `filters.apiInput` into the list query.
   */
  filters?: FilterUnit<object>;
  rowSelection?: RowSelectionState;
  setRowSelection?: (rowSelection: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  debounceSearchValueMilliseconds?: number;
  pageSizeOptions?: number[];
  translations?: NoddiTableTranslations;
  /** Standardized actions for row selection bottom bar. Pass array of actions. Single action renders as button, multiple as dropdown. */
  rowSelectionActions?: RowSelectionAction[];
  /** Placement of action buttons. "bottom" (default) shows full action bar at bottom, "top" shows only action buttons beside filter/view buttons. */
  rowSelectionActionPlacement?: 'top' | 'bottom';
  /** Contextual line shown under the count in the bottom selection bar (e.g. the first selected row's plate and model). Compute from the selected rows in the consumer. */
  rowSelectionSubtitle?: string;
  /**
   * How many rows "Select all N matching" would select, when that is not
   * `totalCount`. A table can list rows it cannot select -- an invoice
   * candidate list shows excluded orders alongside billable ones -- and
   * comparing the selection against every listed row leaves the control on
   * screen forever, because selecting all the selectable ones never reaches it.
   * Mirrors NoddiStaticTable. Defaults to `totalCount`.
   */
  rowSelectionTotalMatchingCount?: number;
  /** When set with row selection, shows "Select all N matching" when fewer than N rows are selected. */
  rowSelectionSelectAllMatching?: {
    onSelectAllMatching: () => void;
    isLoading: boolean;
    /** When set, used instead of `translations.rowSelectionBottomBar.selectAllMatching` (e.g. domain wording without touching translation overrides). */
    selectAllMatchingLabel?: (totalMatchingCount: number) => string;
  };
  /**
   * Stretches the table to fill the remaining height of its parent instead of sizing to content.
   * Requires the parent `NavioPage` to also set `fillHeight` so the flex height chain reaches the table.
   * Use on table-only list pages; leave off when the table shares a page with other content.
   */
  fillHeight?: boolean;
  /** Merged onto the outer table shell (e.g. tighter max-height inside dialogs). */
  className?: string;
  /**
   * Infinite scroll: hide page controls and load the next page when the table body
   * scrolls near the bottom. Pair with `useInfiniteGet` and flattened `data`.
   */
  infiniteScroll?: {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
  };
}

// Keep the no-rows fallback stable so TanStack does not rebuild the row model on every render.
const EMPTY_ROWS: never[] = [];
const NO_CONTROLS: FilterControl[] = [];

export function NoddiTable<TData, TValue, TOrderingKey extends string = string>({
  columns = [],
  data,
  pagination,
  setPagination,
  totalCount,
  isPending,
  isFetching,
  searchPlaceholder = 'Search...',
  getRowId,
  headerNode,
  title,
  titleCount,
  description,
  icon,
  iconTile,
  actions,
  getUrlPath,
  filters,
  rowSelection,
  setRowSelection,
  enableRowSelection,
  debounceSearchValueMilliseconds = 300,
  wrapInCard = false,
  containerClassName,
  headerClassName,
  columnSize,
  pageSizeOptions,
  customNode,
  hideSearch,
  onRowClick,
  onRowContextMenu,
  emptyStateText,
  ordering,
  onOrderChange,
  translations,
  rowSelectionActions,
  rowSelectionActionPlacement = 'bottom',
  rowSelectionSubtitle,
  rowSelectionTotalMatchingCount,
  rowSelectionSelectAllMatching,
  disableRowMenu,
  hideCopyRowId,
  showRowMenuWithSelection,
  fillHeight = false,
  className,
  infiniteScroll
}: NoddiTableProps<TData, TValue, TOrderingKey>) {
  const controls = filters?.controls ?? NO_CONTROLS;
  const isMobile = useIsMobile();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const resolvedTranslations = resolveNoddiTableTranslations(translations);

  const { processedColumns } = useGetProcessedColumns({
    columns,
    enableRowSelection: !!setRowSelection
  });

  const resetPagination = () => setPagination({ ...pagination, pageIndex: 0 });

  const defaultColumnSize = columnSize ? columnSize : isMobile ? 100 : 150;

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack useReactTable returns functions the React Compiler cannot memoize; compiler skipping this component is accepted
  const table = useReactTable({
    data: data ?? EMPTY_ROWS,
    columns: processedColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => String(getRowId(row)),
    state: {
      sorting,
      pagination,
      // improvement from rowSelection || {}. Old one will create a brand-new object every render, so React Table's shallow compare would think state.rowSelection changed and trigger unnecessary recalculations
      ...(rowSelection !== undefined && { rowSelection })
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: (pagination) => setPagination(pagination as NoddiPaginationState),
    enableRowSelection: enableRowSelection ?? !!setRowSelection,
    defaultColumn: { size: defaultColumnSize }
  });

  // A fetch with no rows to show yet (first load, or a refetch after the cached page expired)
  // must read as loading — never as an empty result — even when the consumer omits isPending.
  // `data={x ?? []}` call sites make data [] during the first load, so an empty array only
  // counts as loading while totalCount is also unresolved; a table that has rows keeps showing
  // them during background refetches regardless of whether it ever gets a server count.
  const isInitialLoading =
    (isPending ?? false) || (isFetching && (data === undefined || (data.length === 0 && totalCount === undefined)));
  const isRefetching = isFetching && !isInitialLoading;
  const selectedCount = rowSelection ? Object.keys(rowSelection).length : 0;

  const hasNextPage = infiniteScroll?.hasNextPage;
  const isFetchingNextPage = infiniteScroll?.isFetchingNextPage;
  const fetchNextPage = infiniteScroll?.fetchNextPage;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const hasRowSelectionActions = rowSelectionActions && rowSelectionActions.length > 0;
  const showActionBar = selectedCount > 0 && hasRowSelectionActions;
  const showTopActions = showActionBar && rowSelectionActionPlacement === 'top';
  const showBottomActionBar = showActionBar;

  const clearSelection = () => {
    setRowSelection?.({});
  };

  return (
    <NoddiResponsiveCard
      title={title}
      titleCount={titleCount}
      description={description}
      icon={icon}
      iconTile={iconTile}
      headerNode={headerNode}
      wrapInCard={wrapInCard}
      className={cn(
        'flex flex-col',
        // fillHeight relies on a NavioPage fillHeight parent to provide a bounded flex height chain;
        // otherwise fall back to the viewport-relative cap. The cap is an estimate of the NavioPage
        // header height (smaller on mobile, hence the smaller mobile offset).
        fillHeight ? 'min-h-0 flex-1' : 'max-h-[calc(100vh-9rem)] md:max-h-[calc(100vh-18rem)]',
        className
      )}
      contentClassName='flex min-h-0 flex-1 flex-col'
    >
      <MobileFilterDrawer
        open={filterSidebarOpen}
        onOpenChange={setFilterSidebarOpen}
        controls={controls}
        resetPagination={resetPagination}
        translations={resolvedTranslations.filter}
      />
      <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2'>
        {/* Sticky so search/filters stay below dialog/page headers when an outer container scrolls,
            which needs an opaque fill. `--ambient-surface` is published by whatever paints the
            surface under the table — page canvas, card, dialog — so the strip never renders a band
            in a different color than its host. `headerClassName` still overrides. */}
        <div className={cn('sticky top-0 z-20 shrink-0 bg-ambient-surface', headerClassName)}>
          <TableFilterHeader
            table={table}
            pagination={pagination}
            searchPlaceholder={searchPlaceholder}
            setPagination={setPagination}
            setDateRange={filters?.dateRange?.onChange}
            dateRange={filters?.dateRange?.value}
            dateRangePlaceholder={filters?.dateRange?.placeholder}
            controls={controls}
            quickFilter={filters?.quickFilter}
            debounceSearchValueMilliseconds={debounceSearchValueMilliseconds}
            customNode={customNode}
            hideSearch={hideSearch}
            filterSidebarOpen={filterSidebarOpen}
            setFilterSidebarOpen={setFilterSidebarOpen}
            translations={{
              filter: resolvedTranslations.filter,
              view: resolvedTranslations.view
            }}
            rowSelectionActionButtons={
              showTopActions && rowSelectionActions ? (
                <RowSelectionActionButtons
                  actions={rowSelectionActions}
                  translations={resolvedTranslations.rowSelectionBottomBar}
                />
              ) : undefined
            }
          />
          {/* Fixed height whether or not the bar is showing, so a refetch never shifts the rows.
              The 4px bar sits at the bottom of the slot: that puts 8px between it and the toolbar
              above, and the wrapper's own gap-2 supplies the matching 8px to the rows below.
              Flush against the toolbar it read as an edge of the controls rather than as progress. */}
          <div className='flex h-3 shrink-0'>
            {isRefetching && !infiniteScroll?.isFetchingNextPage && (
              // self-end beats the loader's own self-center, which ignores the slot's alignment.
              <NoddiLinearProgressLoader wrapperClassName='self-end' />
            )}
          </div>
        </div>

        {/* Flex (not h-full) so BaseTable gets a bounded height even under the max-h fallback,
            where percentage heights cannot resolve and would grow to content, clipping the rows. */}
        <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
          <BaseTable
            table={table}
            isPending={isInitialLoading}
            skeletonRowCount={pagination.pageSize}
            containerClassName={containerClassName}
            getUrlPath={getUrlPath}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            emptyStateText={emptyStateText ?? resolvedTranslations.emptyState.noResults}
            actions={actions}
            ordering={ordering}
            onOrderChange={onOrderChange}
            disableRowMenu={disableRowMenu}
            hideCopyRowId={hideCopyRowId}
            showRowMenuWithSelection={showRowMenuWithSelection}
            translations={{
              contextMenu: resolvedTranslations.contextMenu,
              columnHeader: resolvedTranslations.columnHeader,
              pagination: resolvedTranslations.pagination,
              row: resolvedTranslations.row
            }}
            onLoadMore={infiniteScroll ? handleLoadMore : undefined}
            hasMore={infiniteScroll?.hasNextPage}
            isLoadingMore={infiniteScroll?.isFetchingNextPage}
          />
        </div>

        {showBottomActionBar && (
          <RowSelectionActionBar
            selectedCount={selectedCount}
            totalMatchingCount={rowSelectionTotalMatchingCount ?? totalCount}
            actions={rowSelectionActions}
            onClearSelection={clearSelection}
            onSelectAllMatching={rowSelectionSelectAllMatching?.onSelectAllMatching}
            isSelectAllMatchingLoading={rowSelectionSelectAllMatching?.isLoading}
            selectAllMatchingLabel={rowSelectionSelectAllMatching?.selectAllMatchingLabel}
            subtitle={rowSelectionSubtitle}
            translations={resolvedTranslations.rowSelectionBottomBar}
          />
        )}

        {!infiniteScroll && (
          <div className='relative shrink-0'>
            {pagination && (
              <TablePagination
                pageSizeOptions={pageSizeOptions}
                loading={isInitialLoading || isRefetching}
                pagination={pagination}
                totalCount={totalCount}
                onChange={({ pageIndex, pageSize }) =>
                  setPagination({ pageIndex, pageSize, search: pagination.search })
                }
                selectedCount={!!setRowSelection && rowSelection ? Object.keys(rowSelection).length : undefined}
                translations={resolvedTranslations.pagination}
              />
            )}
          </div>
        )}
      </div>
    </NoddiResponsiveCard>
  );
}
